import { CrudStoreItemOnboarding } from 'legacy/lut/classes/models/onboarding/CrudStoreItemOnboarding'
import businessProfileStoreService from 'legacy/lut/classes/services/BusinessProfileStoreService'
import { EMarket } from 'legacy/lut/configs/geo'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { TabButtons } from 'legacy/lut/library/tabButtons'
import { appGetState } from 'legacy/lut/store'

import { createContact } from 'clickNgo/entities/contact/api'
import { getUrlParam } from 'core-components/helpers/url'
import { Button } from 'core-components/library/button'
import { getFormatDate } from 'core/classes/helpers/DateHelper'
import { guid } from 'core/classes/helpers/GuidHelper'
import { siteBuilderGetState } from 'core/sitebuilder/store'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'
import { fail } from 'onboarding/classes/services/notification'
import {
  handleErrorResponse,
  handleFailResponse,
} from 'onboarding/components/widgets/public/forms/helpers/handleResponse'
import { refreshActionRequest } from 'onboarding/features/actions/refreshActionRequestAction'
import { refreshProfilingProgressData } from 'onboarding/features/actions/refreshProgressData'
import { actionApi } from 'onboarding/features/api/actionApi'
import { contactApi } from 'onboarding/features/api/contactApi'
import { refreshContactData } from 'onboarding/features/onboardingSlice'
import { contactService } from 'onboarding/services/contactService'
import { convertErrorMessagesToHTMLList } from 'onboarding/utils/convertErrorMessagesToHTMLList'
import settings from 'src/config/settings'

import { EFormSource } from '../../onboarding/requestsInfo/requestDetail/actionView/dataCollector/FormSelector'
import styles from '../ContactEditDialog.module.scss'
import { mapDataModelToIContact } from '../helpers/mapDataModelToIContact'
import { IContactEditDialogProps, IContactEditDialogState } from '../models'

import { ConfirmationDialog } from './ConfirmationDialog'

interface IDialogHeaderProps {
  state: IContactEditDialogState
  props: IContactEditDialogProps
  changeConfirmationDialog: () => void
  closeDialogAsk: () => void
  onSaveStatus: () => Promise<void>
  onRemoveRequest: () => Promise<void>
  onApply: (callOnUpdate?: boolean) => Promise<void>
  setIsLoading: (value: boolean) => void
  setTab: (value: number) => void
}

export const DialogHeader = ({
  state,
  props,
  changeConfirmationDialog,
  closeDialogAsk,
  onSaveStatus,
  onRemoveRequest,
  onApply,
  setIsLoading,
  setTab,
}: IDialogHeaderProps) => {
  const {
    isLoading,
    entryIdItem,
    fieldsWithErrors,
    modifiedFields,
    validationMessage,
    incorrectValuesMessage,
    showTabs,
    tab,
    readOnly,
    contactWithMultiplePartners,
    replacementContact,
  } = state
  const dispatch = useAppDispatch()

  const handleChangeTab = (value: string = '0') => {
    setTab(+value)
  }

  const onCreateContact = async () => {
    try {
      setIsLoading(true)
      const data = mapDataModelToIContact(entryIdItem)
      const createContactResponse = await createContact(data)
      const { status, message, data: responseData } = createContactResponse

      if (status === 'fail') {
        handleFailResponse(responseData)
        return
      }

      if (status === 'error') {
        handleErrorResponse(message)
        return
      }

      if (status === 'success') {
        const requestId = entryIdItem.get('OnboardingRequestId')
        dispatch(contactApi.getContactsWithMultiplePartners({ requestId }))
        dispatch(refreshActionRequest())
        dispatch(refreshProfilingProgressData())
        dispatch(refreshContactData())
        props.onClose({ isUpdated: true })
      }
    } catch (error) {
      Logger.error('Error in onCreateContact:', error)
      fail('An error occurred while saving.', false)
    } finally {
      setIsLoading(false)
    }
  }

  const saveAsDraft = async () => {
    try {
      setIsLoading(true)

      const cloned = tab === 5 ? replacementContact.clone() : entryIdItem.clone()
      if (props.onboardingRequestId) {
        cloned.set('OnboardingRequestId', props.onboardingRequestId)
      }

      let newContactId = ''
      if (tab === 4 && props.onboardingRequestId) {
        // if the user makes a Relink from the Onboarding request, then swap the values
        cloned.set('PartnerID', entryIdItem.get('NewPartnerID'))
        cloned.set('NewPartnerID', entryIdItem.get('PartnerID'))
      } else if (tab === 5) {
        newContactId = `new-${guid()}`
        cloned.set('ContactID', newContactId)
        cloned.set('WorkflowStatus', 'Draft')
        cloned.set('EntryId', entryIdItem.get('EntryId'))

        const page = siteBuilderGetState().routeParams.page
        const clickAndGoPages = ['newpartners', 'partner-dashboard', 'hpscoutso']
        const isClickAndGoPage =
          clickAndGoPages.some((el) => page.includes(el)) && !page.includes('request')
        const source = isClickAndGoPage ? 'Click&Go' : props.appSource || 'Onboarding'

        if (!cloned.get('Source')) {
          cloned.set('Source', source)
        }

        const result = await businessProfileStoreService.validateContact(cloned)
        if (result.errorMessage) {
          fail(convertErrorMessagesToHTMLList(result.errorMessage), false)
          return
        }
      }

      const saved = await businessProfileStoreService.changeProfileExt(
        cloned,
        'Draft',
        props.appSource,
        appGetState().user.email
      )

      if (saved) {
        if (props.onboardingRequestId && tab === 5) {
          if (
            entryIdItem.get('Market') === EMarket.latinAmerica &&
            entryIdItem.get('PR_HP_ROLE')?.includes('Partner Signer')
          ) {
            const source = CrudStore.get(CrudStoreItemOnboarding.PartnerShipLA)
            const partnerShipLAData = await source.getById({
              id: props.onboardingRequestId,
              force: true,
              keyField: 'RequestId',
            })

            const newItem = {
              ...partnerShipLAData,
              FirstName: cloned.get('FirstName'),
              LastName: cloned.get('LastName'),
              Email: cloned.get('Email'),
              MobilePhone_Prefix: cloned.get('MobilePhone_Prefix'),
              MobilePhone: cloned.get('MobilePhone'),
              WorkPhone_Prefix: cloned.get('WorkPhone_Prefix'),
              WorkPhone: cloned.get('WorkPhone'),
            }
            await source['update'](EFormSource.PartnershipDetailsLA, [newItem])
          } else if (
            entryIdItem.get('Market') === EMarket.northAmerica &&
            entryIdItem.get('PR_HP_ROLE')?.includes('Partner Signer')
          ) {
            const source = CrudStore.get(CrudStoreItemOnboarding.PartnerShipNA)
            const partnerShipNAData = await source.getById({
              id: props.onboardingRequestId,
              force: true,
              keyField: 'RequestId',
            })
            const newItem = {
              ...partnerShipNAData,
              SignatoryFirstName: cloned.get('FirstName'),
              SignatoryLastName: cloned.get('LastName'),
              SignatoryEmail: cloned.get('Email'),
              JobFunction: cloned.get('PrimaryJobRole'),
            }
            await source['update'](EFormSource.PartnershipDetailsNA, [newItem])
          }
          await contactService.updateContactId({
            requestId: props.onboardingRequestId,
            newContactId: newContactId,
            oldContactId: entryIdItem.get('ContactID'),
            partnerId: entryIdItem.get('PartnerID'),
          })
        }
        const requestId = +getUrlParam('rid')
        dispatch(contactApi.getContactsWithMultiplePartners({ requestId }))
        dispatch(actionApi.getExecutableTransitions({ requestId }))
      }

      props.onClose({ isUpdated: Boolean(saved) })
    } catch (error) {
      fail('An error occurred while saving the draft.', false)
    } finally {
      setIsLoading(false)
    }
  }

  const isNewPartner = entryIdItem.get('PartnerID', '').indexOf('new-') === 0

  const isProfileFieldsInvalid = tab === 0 && (!state.validation || !!fieldsWithErrors.length)

  const isContactRelinkingFieldsInvalid =
    tab === 4 &&
    !!['Email', 'FirstName', 'LastName'].find((field) =>
      fieldsWithErrors.find((fieldWithError) => fieldWithError === field)
    )

  const disabledOnApply =
    readOnly ||
    isLoading ||
    isProfileFieldsInvalid ||
    isContactRelinkingFieldsInvalid ||
    (!modifiedFields.length &&
      !state.isUpdateDataTools &&
      !state.isUpdateDataCampaigns &&
      !state.isUpdateDataUserRigth &&
      state.preferredContact.default === state.preferredContact.current)

  let color = '#ffffff'
  let message = ''
  let reason = ''
  let textColor = ''

  const modifiedDate = entryIdItem.get('Modified')
  const formattedDate = getFormatDate(modifiedDate, 'full')
  const reasonDate = !modifiedDate || formattedDate === 'Invalid Date' ? '' : formattedDate

  const maintenance = state.maintenance
  const maintenanceColor = '#ff0000'

  if (entryIdItem) {
    if (entryIdItem.get('WorkflowStatus') === 'New') {
      color = '#cccc00'
      message = 'New'
      const typeOfRequest =
        entryIdItem.get('ContactID', '').indexOf('new-') === 0 ? 'Create' : 'Update'
      reason = modifiedDate ? `Profile ${typeOfRequest} Requested: ${reasonDate}` : ''
    }
    if (entryIdItem.get('WorkflowStatus') === 'Draft') {
      color = '#e0e1e4'
      message = 'Draft'
      textColor = '#000'
    }
    if (entryIdItem.get('WorkflowStatus') === 'WIP') {
      color = '#FFA500'
      message = 'WIP'
      reason = `${reasonDate}`
    }
    if (entryIdItem.get('WorkflowStatus') === 'Rejected') {
      color = '#ff0000'
      message = 'Rejected'
      reason = `Reason: ${entryIdItem.get('ProfileTeamComment')}. Request: ${reasonDate}`
    }
  }
  const isNewContact = entryIdItem.get('ContactID', '').indexOf('new-') === 0
  return (
    <div className={styles.dialogTitle}>
      {settings.appIdentifier.id !== 'hpscoutso' && settings.appIdentifier.id !== 'lut' ? (
        <h6 className={styles.dialogName}>Edit profile</h6>
      ) : (
        <TabButtons
          classes={{ root: styles.tabs }}
          items={[
            {
              id: '0',
              label: isNewContact ? 'Basic info' : 'Edit profile',
              content: () => <></>,
            },
            {
              id: '1',
              label: 'User Right',
              viewCond: !isNewContact && showTabs['UserRights'],
              content: () => <></>,
            },
            {
              id: '2',
              label: 'Campaigns',
              content: () => <></>,
              viewCond: !isNewContact && showTabs['Tools'],
            },
            {
              id: '3',
              label: 'Tool Access',
              content: () => <></>,
              viewCond: !isNewContact && showTabs['Tools'],
            },
            {
              id: '4',
              label: 'Contact Relinking',
              content: () => <></>,
              viewCond:
                (!isNewContact || Boolean(contactWithMultiplePartners)) &&
                showTabs['ContactRelinking'],
            },
            {
              id: '5',
              label: 'Create replacement contact',
              content: () => <></>,
              viewCond:
                Boolean(contactWithMultiplePartners) && showTabs['CreateReplacementContact'],
            },
          ]}
          onChange={handleChangeTab}
          value={tab + ''}
        />
      )}
      <div className={styles.contactHeader}>
        <div className={styles.contactHeaderContent}>
          {!tab ? (
            <span
              className={styles.dialogNameLabel}
              style={{ backgroundColor: `${color}`, color: `${textColor}` }}
            >
              {message}
            </span>
          ) : null}
          <span className={styles.dialogNameLabel} style={{ color: `${maintenanceColor}` }}>
            {maintenance}
          </span>
          <div className={styles.dialogActionsButtons}>
            {!tab &&
            (entryIdItem.get('WorkflowStatus') === 'New' ||
              entryIdItem.get('WorkflowStatus') === 'Rejected' ||
              entryIdItem.get('WorkflowStatus') === 'Draft') ? (
              <Button
                color={'danger'}
                className={styles.dialogActionsItem}
                onClick={changeConfirmationDialog}
                disabled={isLoading || readOnly}
              >
                Remove Request
              </Button>
            ) : null}
            {isNewContact ? (
              <Button
                className={styles.dialogActionsItem}
                id={'cmp-dialog-EPC-button-apply'}
                onClick={saveAsDraft}
                disabled={readOnly}
              >
                Save as Draft
              </Button>
            ) : null}
            <Button
              id="cmp-dialog-EPC-button-apply"
              className={styles.dialogActionsItem}
              onClick={isNewContact ? onCreateContact : onApply}
              disabled={disabledOnApply}
            >
              Apply
            </Button>
          </div>
          {reason && !tab ? <p className={styles.reason}>{reason}</p> : null}
          {!tab ? (
            <ConfirmationDialog
              changeStatus={state.changeStatus}
              confirmationDialog={state.confirmationDialog}
              changeConfirmationDialog={changeConfirmationDialog}
              onSaveStatus={onSaveStatus}
              onRemoveRequest={onRemoveRequest}
            />
          ) : null}
          {validationMessage.length > 0 && !tab ? (
            <>
              <ul className={styles.validations}>
                {validationMessage.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
              <hr className={styles.divider} />
            </>
          ) : null}
          {incorrectValuesMessage && !tab ? (
            <>
              <div className={styles.validations}>{incorrectValuesMessage}</div>
              <hr className={styles.divider} />
            </>
          ) : null}
        </div>
      </div>
      <Button
        className={styles.buttonsClear}
        disabled={isLoading}
        iconButton={'kbase kbase-close'}
        onClick={closeDialogAsk}
        variant={'text'}
        color={'grey'}
      />
    </div>
  )
}
